import React, { FC, ReactElement, useState, useEffect } from 'react';
import { useLocation as UrlLocation } from 'react-router-dom';
import { withStyles } from '@mui/styles';
// Components
import Text from '../text';
import SvgIcon from '../icons/SvgIcon';
// Resources
import { PinPannelProps } from './mapTypes';
import { stringsRes } from '../../resources/strings';
import { getLastPathname, getSuffixWithNumber } from '../../utils/strings';
import { paths } from '../../navigation/routes/paths';
// Style
import styles from './pin-style';
import { useBreakpoints, WebLocation } from '../../customHooks/useBreakpoints';
import { IconAxel, IconCommon } from '../../resources/images';

// Hide or show eta on Destination Pin
const showEtaOnDestination = false;

const PinPanelComponent: FC<PinPannelProps> = ({
    mode,
    address,
    currentBookingQuote,
    isAsap,
    classes,
    drawed,
}): ReactElement => {
    const { pathname } = UrlLocation();
    const { isMobileSize, aboveAll } = useBreakpoints(WebLocation.FLOW);

    const [transformX, setTransformX] = useState(0);
    const [transformY, setTransformY] = useState(-10);

    // Tell if we show the Pin pannel infos depending of the actual page
    function hasPinPannel(): boolean {
        const lastPathname = getLastPathname(pathname);
        const allowedScreens = [paths.homeService, paths.homeSchedule, paths.homeMad].map((p) => getLastPathname(p));
        return !isMobileSize && (allowedScreens.includes(lastPathname) || pathname.includes('confirm'));
    }

    let estimateEta = currentBookingQuote?.estimate_eta;
    if (mode === 'to' && !showEtaOnDestination) {
        estimateEta = null;
    }

    const ref = React.useRef(null);

    useEffect(() => {
        const componentNode = document.getElementById(mode);
        if (componentNode && drawed) {
            const rect = componentNode.getBoundingClientRect();
            const elem = document.getElementById('ride-tracking')?.offsetWidth && aboveAll;
            const positionAndSize = rect.x + 228 + (elem ? 315 : 0);

            setTransformX(0);
            setTransformY(-10);
            // Handle right crossing
            if (positionAndSize > window.innerWidth) {
                setTransformX(-(positionAndSize - window.innerWidth + 40));
            }
            // Handle left crossing
            if (rect.x < 0) {
                setTransformX(rect.x * -1); // Move the element back to the right
            }

            // Handle bottom crossing
            if (rect.y + 56 > window.innerHeight) {
                const overflow = rect.y + rect.height - window.innerHeight + 25;
                setTransformY(-overflow); // Move the element back up
            }

            // Handle top crossing
            if (rect.y < 0) {
                setTransformY(rect.y * -1); // Move the element back down
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawed]);

    const addressLabel = address?.details?.full_name ?? address?.name ?? '';

    if (!hasPinPannel() || !drawed || (!addressLabel && !(estimateEta && isAsap))) {
        return <></>;
    }

    return (
        <>
            <div
                className={classes.pinContainer}
                ref={ref}
                style={{
                    transform: `translate(${
                        transformX === 0 && transformY !== -10 ? 15 : transformX
                    }px, ${transformY}px)`,
                }}
            >
                <div className={classes.pinIconEta}>
                    {estimateEta && isAsap ? (
                        <Text.ShortRegularText12
                            text={getSuffixWithNumber(
                                Math.max(1, Math.round(estimateEta / 60)),
                                stringsRes.time.min,
                                stringsRes.time.mins,
                            )}
                            customColor="bothWhite"
                            className={classes.centerText}
                        />
                    ) : (
                        <>
                            {!isAsap ? (
                                <SvgIcon
                                    icon={IconAxel.CalendarCheck}
                                    stroke="white"
                                    overwritePathStroke
                                    className={classes.buttonIcon}
                                />
                            ) : (
                                <SvgIcon icon={IconCommon.Tick} className={classes.buttonIcon} />
                            )}
                        </>
                    )}
                </div>
                {addressLabel && (
                    <div className={classes.pinAddressContainer}>
                        <p className={classes.pinAddressText}>
                            <strong>{mode === 'from' ? stringsRes.common.from : stringsRes.common.to} </strong>
                            {addressLabel}
                        </p>
                    </div>
                )}
            </div>
        </>
    );
};

export default withStyles(styles)(PinPanelComponent);
